import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Stethoscope, MapPin, Phone, Video, X, PaperPlaneRight, Heartbeat, Drop, Bone, Eye,
  Brain, HandHeart, ForkKnife, User as UserIcon, PersonArmsSpread, Plus, Clock, CalendarCheck, Paperclip, Truck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import MultiImageUploader from "@/components/MultiImageUploader";

const SYSTEM_ICON = {
  heart: Heartbeat, lungs: PersonArmsSpread, kidney: Drop, liver: ForkKnife,
  skin: HandHeart, nerves: Brain, eyes: Eye, bones: Bone, general: UserIcon,
};

export default function Doctors() {
  const { user } = useAuth();
  const [systems, setSystems] = useState([]);
  const [system, setSystem] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("directory"); // directory | consults | appointments
  const [showDoctorForm, setShowDoctorForm] = useState(false);
  const [showConsultForm, setShowConsultForm] = useState(false);
  const [activeConsult, setActiveConsult] = useState(null); // consultation obj
  const [bookFor, setBookFor] = useState(null); // {doctor, consultation?}
  const [myProfile, setMyProfile] = useState(null); // doctor profile if any
  const [consults, setConsults] = useState([]);
  const [appts, setAppts] = useState([]);

  const loadSystems = async () => {
    try { const { data } = await api.get("/doctors/body-systems"); setSystems(data); }
    catch { /* ignore */ }
  };
  const loadDoctors = async () => {
    try {
      const params = {};
      if (system) params.body_system = system;
      if (q) params.condition = q;
      const { data } = await api.get("/doctor-listings", { params });
      setDoctors(data);
    } catch { toast.error("Failed to load doctors"); }
  };
  const loadMine = async () => {
    if (!user || user === false) return;
    try { const { data } = await api.get("/consultations/mine"); setConsults(data); } catch (err) { console.error("load consults failed", err); }
    try { const { data } = await api.get("/appointments/mine"); setAppts(data); } catch (err) { console.error("load appts failed", err); }
  };

  useEffect(() => { loadSystems(); }, []);
  useEffect(() => { const t = setTimeout(loadDoctors, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [system, q]);
  useEffect(() => { loadMine(); /* eslint-disable-next-line */ }, [user, tab]);

  // if the current user is a doctor, expose an online toggle & fetch profile
  useEffect(() => {
    if (!user || user === false) { setMyProfile(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/doctor-listings");
        const mine = data.find((d) => d.user_id === user.id) || null;
        setMyProfile(mine);
      } catch (err) { console.error("load my doctor profile failed", err); }
    })();
  }, [user]);

  const toggleOnline = async () => {
    if (!myProfile) return;
    const next = !myProfile.is_online_now;
    try { await api.post("/doctors/online-status", { is_online_now: next }); setMyProfile({ ...myProfile, is_online_now: next }); toast.success(next ? "You are online" : "You are offline"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#2C4A3B]">Community clinic</div>
          <h1 className="font-heading text-5xl mt-2 tracking-tight">Ask a <em className="text-[#2C4A3B] not-italic">doctor</em>. Openly.</h1>
          <p className="text-[#5C564D] mt-2 max-w-2xl">Post your issue in the right body system. Every specialist online can read, reply, ask for reports, or invite you to their clinic.</p>
        </div>
        <div className="flex gap-2">
          <input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search condition (e.g. diabetes)" className="px-3 py-2 rounded-full border border-[#E5D9CC] bg-[#F9F6F0] text-sm w-64" />
          {user && user !== false && !myProfile && (
            <button data-testid="btn-doc-signup" onClick={() => setShowDoctorForm(true)} className="samaaj-btn-outline text-sm">I&apos;m a doctor</button>
          )}
          {user && user !== false && myProfile && (
            <button data-testid="btn-online-toggle" onClick={toggleOnline} className={myProfile.is_online_now ? "samaaj-btn-primary text-sm" : "samaaj-btn-outline text-sm"}>
              {myProfile.is_online_now ? "Online · click to go offline" : "Go online"}
            </button>
          )}
          {user && user !== false && (
            <button data-testid="btn-new-consult" onClick={() => setShowConsultForm(true)} className="samaaj-btn-primary text-sm flex items-center gap-1"><Plus size={14} weight="bold" /> Ask now</button>
          )}
        </div>
      </div>

      {/* Body System Tiles */}
      <div className="mt-8 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2" data-testid="body-systems">
        <SystemTile label="All" onClick={() => setSystem("")} active={!system} icon={Stethoscope} />
        {systems.map((s) => (
          <SystemTile key={s.id} label={s.label} onClick={() => setSystem(s.id)} active={system === s.id} icon={SYSTEM_ICON[s.id] || Stethoscope} />
        ))}
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-2 border-b border-[#E5D9CC]">
        {[
          ["directory", "Doctors"],
          ["consults", "Consultations"],
          ["appointments", "Appointments"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`doc-tab-${k}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === k ? "border-[#2C4A3B] text-[#2C4A3B]" : "border-transparent text-[#5C564D]"}`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "directory" && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="doctors-grid">
          {doctors.length === 0 ? (
            <div className="samaaj-card p-10 col-span-full text-center text-[#5C564D]">No doctors listed for this filter.</div>
          ) : doctors.map((d) => (
            <div key={d.id} className="samaaj-card p-5" data-testid={`doc-${d.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full overflow-hidden bg-[#F2EBE5] border border-[#E5D9CC] flex items-center justify-center shrink-0">
                  {d.photo_url ? <img src={d.photo_url} alt={d.full_name} className="w-full h-full object-cover" /> : <Stethoscope size={22} weight="duotone" className="text-[#2C4A3B]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-lg text-[#1C1B1A] truncate">Dr. {d.full_name}</div>
                  <div className="text-xs text-[#5C564D] truncate">{d.specialization}{d.years_experience ? ` · ${d.years_experience} yrs` : ""}</div>
                </div>
                {d.is_online_now && <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" title="Online now" />}
              </div>
              <p className="text-sm text-[#5C564D] mt-3 line-clamp-3 min-h-[48px]">{d.bio || "Available for consultation with the community."}</p>
              {d.body_systems?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {d.body_systems.slice(0, 4).map((c) => (<span key={c} className="samaaj-chip text-[10px]">{c}</span>))}
                </div>
              )}
              <div className="mt-3 flex items-center gap-3 text-xs text-[#5C564D]">
                <span className="flex items-center gap-1"><MapPin size={12} /> {d.city}</span>
                <span className="flex items-center gap-1">{d.consultation_mode === "online" ? <><Video size={12} /> online</> : d.consultation_mode === "in-person" ? "in-person" : <><Video size={12} /> online / in-person</>}</span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="font-heading text-lg text-[#D95A41]">${d.consultation_fee || 0} <span className="text-xs text-[#5C564D]">/ consult</span></div>
                <div className="flex gap-1">
                  {d.contact_phone && <a href={`tel:${d.contact_phone}`} className="samaaj-btn-outline text-xs flex items-center gap-1"><Phone size={12} /> Call</a>}
                  {user && user !== false && (
                    <button onClick={() => setBookFor({ doctor: d })} data-testid={`btn-book-${d.id}`} className="samaaj-btn-primary text-xs">Book</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "consults" && (
        <ConsultationsList consults={consults} user={user} onOpen={setActiveConsult} />
      )}

      {tab === "appointments" && (
        <AppointmentsList appts={appts} user={user} refresh={loadMine} />
      )}

      {showDoctorForm && <DoctorForm onClose={() => setShowDoctorForm(false)} systems={systems} onDone={() => { setShowDoctorForm(false); toast.success("Submitted — pending admin review"); }} />}
      {showConsultForm && <ConsultForm systems={systems} onClose={() => setShowConsultForm(false)} onDone={(c) => { setShowConsultForm(false); loadMine(); setTab("consults"); setActiveConsult(c); }} />}
      {activeConsult && <ConsultThread cid={activeConsult.id} user={user} myProfile={myProfile} onClose={() => { setActiveConsult(null); loadMine(); }} onBook={(doctor) => setBookFor({ doctor, consultation: activeConsult })} />}
      {bookFor && <BookAppointment doctor={bookFor.doctor} consultation={bookFor.consultation} onClose={() => setBookFor(null)} onDone={() => { setBookFor(null); loadMine(); setTab("appointments"); }} />}
    </div>
  );
}

function SystemTile({ label, active, onClick, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={`sys-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      className={`p-3 rounded-2xl border text-xs font-semibold flex flex-col items-center gap-1 ${active ? "bg-[#2C4A3B] text-[#F9F6F0] border-[#2C4A3B]" : "bg-[#F2EBE5] border-[#E5D9CC] text-[#2C4A3B] hover:bg-[#EFE4D3]"}`}
      style={{ transition: "background-color 180ms ease-out, color 180ms ease-out" }}
    >
      <Icon size={20} weight={active ? "fill" : "duotone"} />
      <span className="text-[10px] leading-tight text-center">{label}</span>
    </button>
  );
}

function ConsultationsList({ consults, user, onOpen }) {
  if (!user || user === false) return <div className="mt-6 samaaj-card p-8 text-center text-[#5C564D]">Log in to see or start consultations.</div>;
  if (consults.length === 0) return <div className="mt-6 samaaj-card p-8 text-center text-[#5C564D]">No consultations yet. Tap “Ask now” to post your first issue.</div>;
  return (
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="consults-list">
      {consults.map((c) => {
        const msgs = c.messages || [];
        const last = msgs[msgs.length - 1];
        return (
          <button key={c.id} onClick={() => onOpen(c)} data-testid={`consult-${c.id}`} className="samaaj-card p-5 text-left hover:border-[#2C4A3B]">
            <div className="flex items-center justify-between">
              <span className="samaaj-chip text-[10px] uppercase">{c.body_system}</span>
              <span className={`text-[10px] font-bold ${c.status === "answered" ? "text-emerald-700" : "text-[#D49A36]"}`}>{c.status}</span>
            </div>
            <div className="font-heading text-lg mt-2 text-[#1C1B1A] line-clamp-1">{c.title}</div>
            <div className="text-xs text-[#5C564D] mt-1">by {c.patient_name}</div>
            <div className="text-sm text-[#1C1B1A] mt-2 line-clamp-2">{last?.text || c.story}</div>
            <div className="mt-2 text-[10px] text-[#5C564D]">{msgs.length} message{msgs.length !== 1 ? "s" : ""}</div>
          </button>
        );
      })}
    </div>
  );
}

function AppointmentsList({ appts, user, refresh }) {
  if (!user || user === false) return <div className="mt-6 samaaj-card p-8 text-center text-[#5C564D]">Log in to see your appointments.</div>;
  if (appts.length === 0) return <div className="mt-6 samaaj-card p-8 text-center text-[#5C564D]">No appointments yet.</div>;
  return (
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="appts-list">
      {appts.map((a) => <AppointmentCard key={a.id} a={a} user={user} refresh={refresh} />)}
    </div>
  );
}

function AppointmentCard({ a, user, refresh }) {
  const isPatient = a.patient_id === user.id;
  const isDoctor = a.doctor_id === user.id;
  const [busy, setBusy] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [counterOpen, setCounterOpen] = useState(false);
  const [proposedAt, setProposedAt] = useState("");
  const [note, setNote] = useState("");
  const canCancel = ["requested", "confirmed", "rescheduled"].includes(a.status);
  const canConfirm = isDoctor && a.status === "requested";
  const canComplete = isDoctor && ["confirmed", "rescheduled"].includes(a.status);
  const canPropose = ["requested", "confirmed", "rescheduled"].includes(a.status);
  const pending = a.pending_reschedule;
  const pendingByMe = pending && pending.proposed_by === user.id;
  const pendingWaitingMe = pending && pending.proposed_by && pending.proposed_by !== user.id;
  const act = async (action) => {
    setBusy(true);
    try { await api.post("/appointments/update", { id: a.id, action }); toast.success(`Appointment ${action}ed`); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const proposeReschedule = async () => {
    if (!proposedAt) { toast.error("Pick a new date/time"); return; }
    setBusy(true);
    try {
      await api.post("/appointments/propose-reschedule", { id: a.id, scheduled_at: new Date(proposedAt).toISOString(), note });
      toast.success("Reschedule proposed"); setProposeOpen(false); setProposedAt(""); setNote(""); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const respondReschedule = async (action, sched, n) => {
    setBusy(true);
    try {
      await api.post("/appointments/respond-reschedule", { id: a.id, action, scheduled_at: sched ? new Date(sched).toISOString() : null, note: n || "" });
      toast.success(`Reschedule ${action}ed`); setCounterOpen(false); setProposedAt(""); setNote(""); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const win = a.other_city && isDoctor ? "24h" : "2h";
  return (
    <div className="samaaj-card p-5" data-testid={`appt-${a.id}`}>
      <div className="flex items-center justify-between">
        <span className="samaaj-chip text-[10px] uppercase">{a.mode}</span>
        <span className={`text-[10px] font-bold ${a.status === "confirmed" ? "text-emerald-700" : a.status === "cancelled" ? "text-red-600" : "text-[#D49A36]"}`}>{a.status.replace(/_/g, " ")}</span>
      </div>
      <div className="font-heading text-lg mt-2 text-[#1C1B1A]">{isPatient ? `Dr. ${a.doctor_name}` : a.patient_name}</div>
      <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><Clock size={12} /> {new Date(a.scheduled_at).toLocaleString()}</div>
      {a.doctor_city && <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><MapPin size={12} /> {a.doctor_city}{a.other_city ? " · other city" : ""}</div>}
      {a.notes && <div className="text-xs text-[#1C1B1A] mt-2">{a.notes}</div>}
      <div className="text-[10px] text-[#5C564D] mt-2">Cancel window: {win} before start</div>

      {pending && (
        <div className="mt-3 p-3 rounded-2xl bg-[#D49A36]/15 border border-[#D49A36]/40 text-xs text-[#5C4110] space-y-2">
          <div className="font-bold">{pending.proposed_by_role === "doctor" ? "Doctor" : "Patient"} proposed a new time</div>
          <div className="flex items-center gap-1"><Clock size={11} /> {new Date(pending.proposed_scheduled_at).toLocaleString()}</div>
          {pending.note && <div className="italic">“{pending.note}”</div>}
          {pendingByMe && <div className="text-[10px] text-[#5C564D]">Awaiting response…</div>}
          {pendingWaitingMe && (
            <div className="flex flex-wrap gap-2 mt-1">
              <button disabled={busy} onClick={() => respondReschedule("accept")} data-testid={`btn-resched-accept-${a.id}`} className="samaaj-btn-primary text-xs">Accept</button>
              <button disabled={busy} onClick={() => setCounterOpen(true)} data-testid={`btn-resched-counter-${a.id}`} className="samaaj-btn-outline text-xs">Counter-propose</button>
              <button disabled={busy} onClick={() => respondReschedule("reject")} data-testid={`btn-resched-reject-${a.id}`} className="samaaj-btn-outline text-xs">Reject</button>
            </div>
          )}
        </div>
      )}

      {(proposeOpen || counterOpen) && (
        <div className="mt-3 p-3 rounded-2xl border border-[#E5D9CC] bg-[#F2EBE5] space-y-2">
          <div className="text-xs font-bold text-[#2C4A3B]">{counterOpen ? "Counter-propose new time" : "Propose new time"}</div>
          <input type="datetime-local" value={proposedAt} onChange={(e) => setProposedAt(e.target.value)} data-testid={`resched-time-${a.id}`} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input placeholder="Reason (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" />
          <div className="flex gap-2">
            <button disabled={busy} onClick={counterOpen ? () => respondReschedule("counter", proposedAt, note) : proposeReschedule} data-testid={`btn-resched-send-${a.id}`} className="samaaj-btn-primary text-xs">Send</button>
            <button onClick={() => { setProposeOpen(false); setCounterOpen(false); setProposedAt(""); setNote(""); }} className="samaaj-btn-outline text-xs">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {canConfirm && <button disabled={busy} onClick={() => act("confirm")} data-testid={`btn-confirm-${a.id}`} className="samaaj-btn-primary text-xs">Confirm</button>}
        {canComplete && <button disabled={busy} onClick={() => act("complete")} data-testid={`btn-complete-${a.id}`} className="samaaj-btn-outline text-xs">Mark done</button>}
        {canPropose && !pending && !proposeOpen && <button disabled={busy} onClick={() => setProposeOpen(true)} data-testid={`btn-propose-${a.id}`} className="samaaj-btn-outline text-xs">Propose new time</button>}
        {canCancel && <button disabled={busy} onClick={() => act("cancel")} data-testid={`btn-cancel-${a.id}`} className="samaaj-btn-outline text-xs">Cancel</button>}
      </div>
    </div>
  );
}

function DoctorForm({ onClose, systems, onDone }) {
  const [f, setF] = useState({ full_name: "", specialization: "", body_systems: [], conditions: "", languages: "", city: "", consultation_mode: "both", consultation_fee: 15, years_experience: 5, bio: "", contact_phone: "", photo_url: "", slot_minutes: 30, timezone_offset_minutes: -new Date().getTimezoneOffset(), availability: [] });
  const [busy, setBusy] = useState(false);
  const toggleSys = (id) => setF((prev) => ({ ...prev, body_systems: prev.body_systems.includes(id) ? prev.body_systems.filter(x => x !== id) : [...prev.body_systems, id] }));
  const addSlot = (dow) => setF((p) => ({ ...p, availability: [...p.availability, { day_of_week: dow, start: "09:00", end: "13:00" }] }));
  const updateSlot = (i, field, value) => setF((p) => ({ ...p, availability: p.availability.map((s, idx) => idx === i ? { ...s, [field]: value } : s) }));
  const removeSlot = (i) => setF((p) => ({ ...p, availability: p.availability.filter((_, idx) => idx !== i) }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/doctor-listings", {
        ...f, consultation_fee: Number(f.consultation_fee), years_experience: Number(f.years_experience),
        slot_minutes: Number(f.slot_minutes), timezone_offset_minutes: Number(f.timezone_offset_minutes),
        conditions: f.conditions.split(",").map((s) => s.trim()).filter(Boolean),
        languages: f.languages.split(",").map((s) => s.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const DOWS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-lg w-full p-6 bg-[#F9F6F0] my-8">
        <div className="flex items-start justify-between">
          <div className="font-heading text-3xl">List yourself</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3">
          <input data-testid="doc-name" required placeholder="Dr. Full Name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input data-testid="doc-spec" required placeholder="Specialization (e.g. Cardiology)" value={f.specialization} onChange={(e) => setF({ ...f, specialization: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <div className="col-span-2">
            <div className="text-xs text-[#5C564D] mb-2">Body systems you treat</div>
            <div className="flex flex-wrap gap-2">
              {systems.map((s) => (
                <button type="button" key={s.id} onClick={() => toggleSys(s.id)} data-testid={`sys-toggle-${s.id}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${f.body_systems.includes(s.id) ? "bg-[#2C4A3B] text-[#F9F6F0] border-[#2C4A3B]" : "bg-[#F2EBE5] text-[#2C4A3B] border-[#E5D9CC]"}`}>{s.label}</button>
              ))}
            </div>
          </div>
          <input data-testid="doc-conditions" placeholder="Conditions (comma sep)" value={f.conditions} onChange={(e) => setF({ ...f, conditions: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input data-testid="doc-city" required placeholder="Clinic city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input data-testid="doc-exp" type="number" placeholder="Years exp" value={f.years_experience} onChange={(e) => setF({ ...f, years_experience: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <select data-testid="doc-mode" value={f.consultation_mode} onChange={(e) => setF({ ...f, consultation_mode: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]">
            <option value="both">Online + in-person</option>
            <option value="online">Online only</option>
            <option value="in-person">In-person only</option>
          </select>
          <input data-testid="doc-fee" type="number" step="0.01" placeholder="Consultation fee (USD)" value={f.consultation_fee} onChange={(e) => setF({ ...f, consultation_fee: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input data-testid="doc-phone" placeholder="Contact phone" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <textarea data-testid="doc-bio" rows={3} placeholder="Short bio" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />

          <div className="col-span-2 border-t border-[#E5D9CC] pt-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-bold text-[#2C4A3B]">Weekly availability</div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[#5C564D]">Slot</span>
                <select value={f.slot_minutes} onChange={(e) => setF({ ...f, slot_minutes: e.target.value })} data-testid="doc-slot-minutes" className="px-2 py-1 rounded-lg border border-[#E5D9CC]">
                  <option value={15}>15 min</option><option value={30}>30 min</option><option value={45}>45 min</option><option value={60}>60 min</option>
                </select>
              </div>
            </div>
            <p className="text-[10px] text-[#5C564D] mt-1">Add the windows patients can book. Times are in your local timezone.</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {DOWS.map((d, i) => (
                <button key={i} type="button" onClick={() => addSlot(i)} data-testid={`add-dow-${i}`} className="samaaj-btn-outline text-[10px] px-2 py-1">+ {d}</button>
              ))}
            </div>
            <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
              {f.availability.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-xs" data-testid={`slot-row-${i}`}>
                  <select value={s.day_of_week} onChange={(e) => updateSlot(i, "day_of_week", Number(e.target.value))} className="px-2 py-1 rounded-lg border border-[#E5D9CC]">
                    {DOWS.map((d, k) => (<option key={k} value={k}>{d}</option>))}
                  </select>
                  <input type="time" value={s.start} onChange={(e) => updateSlot(i, "start", e.target.value)} className="px-2 py-1 rounded-lg border border-[#E5D9CC]" />
                  <span>–</span>
                  <input type="time" value={s.end} onChange={(e) => updateSlot(i, "end", e.target.value)} className="px-2 py-1 rounded-lg border border-[#E5D9CC]" />
                  <button type="button" onClick={() => removeSlot(i)} className="text-[#D95A41]" data-testid={`slot-remove-${i}`}><X size={14} /></button>
                </div>
              ))}
              {f.availability.length === 0 && <div className="text-[10px] text-[#5C564D] italic">No windows added. Patients will fall back to picking any date/time.</div>}
            </div>
          </div>

          <button data-testid="doc-submit" disabled={busy} className="samaaj-btn-primary col-span-2">{busy ? "Sending…" : "Submit for review"}</button>
        </form>
      </div>
    </div>
  );
}

function ConsultForm({ systems, onClose, onDone }) {
  const [f, setF] = useState({ body_system: systems[0]?.id || "general", title: "", story: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/consultations", f); toast.success("Posted — specialists have been notified"); onDone(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-lg w-full p-6 bg-[#F9F6F0] my-8">
        <div className="flex items-start justify-between">
          <div className="font-heading text-3xl">Ask the community doctors</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#5C564D] mt-1">Any specialist online in this body system can respond. Multiple doctors may weigh in.</p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <select required value={f.body_system} data-testid="consult-system" onChange={(e) => setF({ ...f, body_system: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]">
            {systems.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
          </select>
          <input required maxLength={140} data-testid="consult-title" placeholder="Short title (e.g. Chest pain after climbing stairs)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <textarea required rows={6} data-testid="consult-story" placeholder="Describe what you feel, how long, any medicines you take…" value={f.story} onChange={(e) => setF({ ...f, story: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <button data-testid="consult-submit" disabled={busy} className="samaaj-btn-primary w-full">{busy ? "Posting…" : "Post consultation"}</button>
        </form>
      </div>
    </div>
  );
}

function ConsultThread({ cid, user, myProfile, onClose, onBook }) {
  const [consult, setConsult] = useState(null);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/consultations/${cid}`); setConsult(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onClose(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const bookWithDoctor = async (doctor_user_id, fallback_name) => {
    try {
      const { data } = await api.get("/doctor-listings");
      const doc = data.find((d) => d.user_id === doctor_user_id);
      if (doc) { onBook(doc); }
      else onBook({ user_id: doctor_user_id, full_name: fallback_name || "Doctor", city: "", specialization: "", consultation_mode: "both" });
    } catch { onBook({ user_id: doctor_user_id, full_name: fallback_name || "Doctor", city: "", specialization: "", consultation_mode: "both" }); }
  };

  if (!consult) return null;
  const isPatient = consult.patient_id === user.id;
  const isDoctor = !!myProfile && (myProfile.body_systems || []).includes(consult.body_system);
  const messages = consult.messages && consult.messages.length ? consult.messages : [{ author_role: "patient", author_name: consult.patient_name, text: consult.story, created_at: consult.created_at, kind: "text", attachments: [] }];

  const send = async (kind = "text") => {
    if (!text && attachments.length === 0 && kind === "text") { toast.error("Type a message or attach reports"); return; }
    setBusy(true);
    try {
      await api.post("/consultations/message", { consultation_id: cid, text, kind, attachments });
      setText(""); setAttachments([]); setShowUpload(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendReportsRequest = async () => {
    setBusy(true);
    try {
      await api.post("/consultations/message", { consultation_id: cid, text: text || "Please upload your recent reports.", kind: "reports_request", attachments: [] });
      setText(""); await load(); toast.success("Reports requested");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendClinicCall = async () => {
    setBusy(true);
    try {
      await api.post("/consultations/message", { consultation_id: cid, text: text || "Please visit my clinic for an in-person check.", kind: "clinic_call", attachments: [] });
      setText(""); await load(); toast.success("Patient asked to visit clinic");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendFollowUp = async () => {
    if (!followUpDate) { toast.error("Pick a due date"); return; }
    setBusy(true);
    try {
      await api.post("/consultations/follow-up", {
        consultation_id: cid,
        due_at: new Date(followUpDate).toISOString(),
        instructions: followUpNote || "Please share follow-up reports before the due date.",
      });
      setFollowUpDate(""); setFollowUpNote(""); setFollowUpOpen(false);
      await load(); toast.success("Follow-up requested");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadReports = async () => {
    if (attachments.length === 0) { toast.error("Add at least one report image"); return; }
    setBusy(true);
    try {
      await api.post("/consultations/message", { consultation_id: cid, text: text || "Uploaded reports.", kind: "reports_uploaded", attachments });
      setText(""); setAttachments([]); setShowUpload(false); await load();
      toast.success("Reports shared");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-2xl w-full p-6 bg-[#F9F6F0] my-8" data-testid={`consult-thread-${cid}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">{consult.body_system}</div>
            <div className="font-heading text-2xl mt-1">{consult.title}</div>
            <div className="text-xs text-[#5C564D] mt-1">by {consult.patient_name}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>

        <div className="mt-4 space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="consult-messages">
          {messages.map((m, i) => {
            const isMine = m.author_id === user.id;
            const isDoc = m.author_role === "doctor";
            const isSys = m.author_role === "system";
            const bubble = isSys
              ? "bg-[#F2EBE5] border-dashed border-[#5C564D]/40 text-center italic"
              : isDoc ? "bg-[#2C4A3B]/10 border-[#2C4A3B]/30" : "bg-[#F2EBE5] border-[#E5D9CC]";
            const msgKey = m.id || m.created_at || `msg-${i}`;
            return (
              <div key={msgKey} className={`p-3 rounded-2xl border ${bubble} ${isSys ? "mx-4" : (isMine ? "ml-6" : "mr-6")}`}>
                {!isSys && (
                  <div className="text-[10px] font-bold text-[#2C4A3B] flex items-center gap-2">
                    {isDoc ? `Dr. ${m.author_name}` : m.author_name}
                    {m.specialization && <span className="samaaj-chip text-[9px]">{m.specialization}</span>}
                    {m.kind === "reports_request" && <span className="samaaj-chip text-[9px] bg-[#D49A36]/20 text-[#7a5a10]">Reports requested</span>}
                    {m.kind === "reports_uploaded" && <span className="samaaj-chip text-[9px] bg-emerald-100 text-emerald-800">Reports</span>}
                    {m.kind === "clinic_call" && <span className="samaaj-chip text-[9px] bg-[#D95A41]/20 text-[#8a2a15]">Clinic visit</span>}
                    {m.kind === "follow_up_request" && <span className="samaaj-chip text-[9px] bg-[#D49A36]/20 text-[#7a5a10]">Follow-up · due {new Date(m.due_at || "").toLocaleDateString()}</span>}
                  </div>
                )}
                {isSys && <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#5C564D]">— visit log —</div>}
                {m.text && <div className="text-sm text-[#1C1B1A] mt-1 whitespace-pre-wrap">{m.text}</div>}
                {m.attachments?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {m.attachments.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-lg overflow-hidden border border-[#E5D9CC]">
                        <img src={url} alt="report" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
                {m.kind === "clinic_call" && isPatient && (
                  <button onClick={() => bookWithDoctor(m.author_id, m.author_name)} data-testid={`btn-book-from-msg-${i}`} className="mt-2 samaaj-btn-primary text-xs flex items-center gap-1"><CalendarCheck size={12} /> Book with Dr. {m.author_name}</button>
                )}
                <div className="text-[10px] text-[#5C564D] mt-1">{new Date(m.created_at).toLocaleString()}</div>
              </div>
            );
          })}
        </div>

        {consult.reports_requested && isPatient && !showUpload && (
          <div className="mt-3 p-3 rounded-2xl bg-[#D49A36]/15 border border-[#D49A36]/40 text-sm text-[#7a5a10] flex items-center justify-between gap-2">
            <span>A doctor has asked for your reports.</span>
            <button onClick={() => setShowUpload(true)} className="samaaj-btn-primary text-xs">Upload now</button>
          </div>
        )}

        {showUpload && isPatient && (
          <div className="mt-3 p-3 rounded-2xl border border-[#E5D9CC] bg-[#F2EBE5]">
            <div className="text-xs font-bold text-[#2C4A3B] mb-2">Upload your reports</div>
            <MultiImageUploader value={attachments} onChange={setAttachments} max={8} testid="reports" label="Reports (photos/scans)" />
            <div className="mt-2 flex gap-2">
              <button disabled={busy || attachments.length === 0} onClick={uploadReports} data-testid="btn-upload-reports" className="samaaj-btn-primary text-sm">Send reports</button>
              <button onClick={() => { setShowUpload(false); setAttachments([]); }} className="samaaj-btn-outline text-sm">Cancel</button>
            </div>
          </div>
        )}

        {followUpOpen && isDoctor && (
          <div className="mt-3 p-3 rounded-2xl border border-[#E5D9CC] bg-[#F2EBE5]" data-testid="follow-up-panel">
            <div className="text-xs font-bold text-[#2C4A3B] mb-2">Set a follow-up</div>
            <input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} data-testid="follow-up-date" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" />
            <textarea rows={2} value={followUpNote} onChange={(e) => setFollowUpNote(e.target.value)} placeholder="Instructions (e.g. Repeat CBC and share reports)" className="w-full mt-2 px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" />
            <div className="mt-2 flex gap-2">
              <button disabled={busy} onClick={sendFollowUp} data-testid="btn-follow-up-send" className="samaaj-btn-primary text-sm">Request follow-up</button>
              <button onClick={() => { setFollowUpOpen(false); setFollowUpDate(""); setFollowUpNote(""); }} className="samaaj-btn-outline text-sm">Cancel</button>
            </div>
          </div>
        )}

        {consult.follow_up_due_at && (
          <div className="mt-3 p-3 rounded-2xl bg-[#D49A36]/10 border border-[#D49A36]/40 text-xs text-[#5C4110] flex items-center justify-between gap-2">
            <span>Follow-up due <b>{new Date(consult.follow_up_due_at).toLocaleDateString()}</b></span>
            {isPatient && <button onClick={() => setShowUpload(true)} className="samaaj-btn-primary text-xs">Upload reports</button>}
          </div>
        )}

        {/* Composer */}
        {(isPatient || isDoctor) && (
          <div className="mt-4 border-t border-[#E5D9CC] pt-3">
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} data-testid="consult-input" placeholder={isPatient ? "Reply, ask a follow-up…" : "Add a consultation note…"} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] bg-white" />
            <div className="mt-2 flex flex-wrap gap-2 justify-end">
              {isPatient && !showUpload && (
                <button onClick={() => setShowUpload(true)} data-testid="btn-attach" className="samaaj-btn-outline text-xs flex items-center gap-1"><Paperclip size={12} /> Attach reports</button>
              )}
              {isDoctor && !isPatient && (
                <>
                  <button disabled={busy} onClick={sendReportsRequest} data-testid="btn-request-reports" className="samaaj-btn-outline text-xs">Request reports</button>
                  <button disabled={busy} onClick={sendClinicCall} data-testid="btn-clinic-call" className="samaaj-btn-outline text-xs flex items-center gap-1"><CalendarCheck size={12} /> Invite to clinic</button>
                  <button disabled={busy} onClick={() => setFollowUpOpen(true)} data-testid="btn-follow-up" className="samaaj-btn-outline text-xs">Set follow-up</button>
                </>
              )}
              <button disabled={busy || !text} onClick={() => send("text")} data-testid="btn-send-msg" className="samaaj-btn-primary text-xs flex items-center gap-1"><PaperPlaneRight size={12} /> Send</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BookAppointment({ doctor, consultation, onClose, onDone }) {
  const [scheduled_at, setSchedAt] = useState("");
  const [mode, setMode] = useState(doctor.consultation_mode === "online" ? "online" : "in-person");
  const [notes, setNotes] = useState("");
  const [patient_city, setPatientCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [slots, setSlots] = useState(null); // null = not loaded, [] = published but empty, list = available
  const [manual, setManual] = useState(false);
  const other_city = useMemo(() => patient_city && patient_city.trim().toLowerCase() !== (doctor.city || "").trim().toLowerCase(), [patient_city, doctor.city]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/doctor-listings/${doctor.user_id}/slots`, { params: { days: 14 } });
        setSlots(data.slots || []);
      } catch { setSlots([]); }
    })();
  }, [doctor.user_id]);

  const slotsByDate = useMemo(() => {
    const map = new Map();
    (slots || []).forEach((iso) => {
      const d = new Date(iso);
      const key = d.toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(iso);
    });
    return map;
  }, [slots]);

  const submit = async (e) => {
    e.preventDefault();
    if (!scheduled_at) { toast.error("Pick a date and time"); return; }
    setBusy(true);
    try {
      const iso = manual ? new Date(scheduled_at).toISOString() : scheduled_at;
      await api.post("/appointments", { doctor_id: doctor.user_id, consultation_id: consultation?.id || null, scheduled_at: iso, mode, notes, patient_city, other_city });
      toast.success("Appointment requested"); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const showSlotPicker = slots !== null && slots.length > 0 && !manual;
  const noSlots = slots !== null && slots.length === 0;

  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-md w-full p-6 bg-[#F9F6F0] my-8">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#2C4A3B]">Book appointment</div>
            <div className="font-heading text-2xl mt-1">Dr. {doctor.full_name}</div>
            <div className="text-xs text-[#5C564D]">{doctor.specialization} · {doctor.city}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          {slots === null && <div className="text-xs text-[#5C564D]">Loading available slots…</div>}
          {showSlotPicker && (
            <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1" data-testid="slot-picker">
              {[...slotsByDate.entries()].map(([dateKey, isoList]) => (
                <div key={dateKey}>
                  <div className="text-xs font-bold text-[#2C4A3B] mb-1">{dateKey}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {isoList.map((iso) => (
                      <button
                        type="button"
                        key={iso}
                        onClick={() => setSchedAt(iso)}
                        data-testid={`slot-${iso}`}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${scheduled_at === iso ? "bg-[#2C4A3B] text-[#F9F6F0] border-[#2C4A3B]" : "bg-[#F2EBE5] text-[#2C4A3B] border-[#E5D9CC]"}`}
                      >
                        {new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setManual(true)} className="text-xs underline text-[#2C4A3B]">Or request a custom time</button>
            </div>
          )}
          {(noSlots || manual) && (
            <>
              {noSlots && <div className="text-xs text-[#7a5a10] bg-[#D49A36]/15 rounded-xl p-2">This doctor hasn&apos;t published weekly slots yet — pick any time and they&apos;ll confirm.</div>}
              <input required type="datetime-local" value={scheduled_at} data-testid="appt-time" onChange={(e) => setSchedAt(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
            </>
          )}
          <select value={mode} data-testid="appt-mode" onChange={(e) => setMode(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]">
            <option value="in-person">In-person visit</option>
            {doctor.consultation_mode !== "in-person" && <option value="online">Online</option>}
          </select>
          <input placeholder="Your city" value={patient_city} data-testid="appt-city" onChange={(e) => setPatientCity(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          {other_city && <div className="text-xs text-[#7a5a10] bg-[#D49A36]/15 rounded-xl p-2">Different city detected — doctor cancellations require ≥ 24 hours notice.</div>}
          <textarea rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <div className="text-[11px] text-[#5C564D]">Both patient and doctor can cancel up to <b>2 hours</b> before the slot ({other_city ? "doctor: 24 hours" : "same-city"}). </div>
          <div className="text-[11px] text-[#2C4A3B] font-semibold flex items-center gap-1"><Truck size={12} /> Pay at the clinic — no card needed.</div>
          <button data-testid="btn-appt-submit" disabled={busy || !scheduled_at} className="samaaj-btn-primary w-full">{busy ? "Requesting…" : "Request appointment"}</button>
        </form>
      </div>
    </div>
  );
}
